import type { StepWithValidation } from '@/hooks/useStepsWithValidation'

/**
 * A file's processing state, resolved once so the File Status badge, the generation
 * trigger and the change-primary picker cannot describe the same file differently.
 *
 * Three copies of this ladder had drifted: one ranked a failed transcode above
 * `inProcess`, the badge and the picker ranked it below, and `hasError` at the row
 * actions and in DeliveryModeSection left transcode failures out of "failed" entirely —
 * which quietly exempted the one failure mode only media files have from decision 9.
 */
export type StepStatus = 'failed' | 'analyzing' | 'preparing' | 'transcodeFailed' | 'unreviewed' | 'checking' | 'ready'

export function getStepStatus(step: StepWithValidation): StepStatus {
  if (step.processProgressError) return 'failed'
  // A re-upload over a failed transcode is working, not failed: the flags come from
  // different queries, so the previous attempt's result can still be sitting on the step.
  if (step.inProcess) return 'analyzing'
  if (step.transcodeCompleted === false) return 'preparing'
  if (step.transcodeSuccess === false) return 'transcodeFailed'
  if (!step.valid) return 'unreviewed'
  // Nothing known about the file yet, shown as "Checking…". Counting it ready would let
  // a still-processing file through.
  if (step.statusPending) return 'checking'
  return 'ready'
}

const FAILED_STATUSES: StepStatus[] = ['failed', 'transcodeFailed']

/**
 * Whether the file has to be replaced rather than waited out.
 *
 * **A failed transcode counts** — decision 9. It is the only failure a video has, and
 * reading just `process_progress` left that decision inert for exactly the files Media
 * exists for.
 */
export function hasStepFailed(step: StepWithValidation): boolean {
  return FAILED_STATUSES.includes(getStepStatus(step))
}

/**
 * What to tell an expert whose primary file failed. The repair depends on whether the
 * module has other files, because decision 7 only allows deleting a failed primary when it
 * is the only one: with siblings the route is Change, and deleting would hand the lead slot
 * to a file nobody chose; alone, Delete empties the slot and Add Primary File comes back.
 *
 * Shared so the Delivery Mode alert and the row tooltips cannot offer different repairs
 * for the same file — and so neither can offer one that isn't there. "Upload it again" was
 * in both for a case with no upload route at all.
 */
export function primaryFailureRepair(hasOtherFiles: boolean): string {
  return hasOtherFiles ? 'Use Change to pick another file to lead.' : 'Delete it and upload another.'
}

/**
 * Whether a file's Delete action is available — decision 7.
 *
 * The primary is swapped, not deleted, so the module always leads with something. A
 * primary that failed to process is the exception: it cannot be swapped in usefully and
 * has to be removable so the expert can upload a replacement.
 *
 * The exception is narrowed to the only-file case on purpose. Deleting a failed primary
 * that has siblings does not empty the slot — the backend promotes the first remaining file
 * (`_ensure_primary_step_id`), which keeps the access it had as a supplementary file and
 * gets no delivery-mode correction, unlike a swap (decision 29). A promoted Media Only file
 * leads a module from outside RAG with nothing in the UI saying so, since the primary table
 * has no Content Access column. Change is the route whenever there is something to change
 * to, and it persists both corrections; alone, Delete is safe because nothing is promoted.
 *
 * No expert is stranded: supplementary files are always deletable, so a module whose other
 * files are all broken can be reduced to the only-file case and then re-uploaded.
 */
export function canDeleteStep({
  isPrimary,
  hasError,
  isOnlyFile,
}: {
  isPrimary: boolean
  hasError: boolean
  isOnlyFile: boolean
}): boolean {
  if (!isPrimary) return true
  return hasError && isOnlyFile
}
